import html2canvas from 'html2canvas';

// Classes used by STAT Testing trend arrows, whose colors must be preserved on export
const STAT_TESTING_ARROW_CLASSES = [
  'biggest-green-arrow',
  'biggest-red-arrow',
  'biggest-yellow-arrow',
];

// Determines whether an svg belongs to a STAT Testing arrow (itself or an ancestor)
const isStatTestingArrow = (svg: SVGElement): boolean =>
  STAT_TESTING_ARROW_CLASSES.some(
    (className) => svg.classList.contains(className) || svg.closest(`.${className}`) !== null
  );

// Menus whose icons must not be rendered in the exported image
const NON_EXPORTABLE_MENU_SELECTORS = ['.meatball-export-menu', '.meatball-menu', '.calendar'];

// Removes the svg icons of the export/calendar menus from the cloned node
const hideNonExportableMenuIcons = (clone: HTMLElement): void => {
  clone
    .querySelectorAll(NON_EXPORTABLE_MENU_SELECTORS.map((selector) => `${selector} svg`).join(', '))
    .forEach((svg) => svg.remove());
};

// Export DynamicTable widget to PNG or Jpeg
export const exportToImage = async (
  name = 'Chart',
  isJpeg = false,
  widgetId: string | undefined,
  isChart = false
) => {
  try {
    if (widgetId === undefined || widgetId === null) {
      return;
    }

    if (isChart) {
      await handleCapture(widgetId, name, isJpeg);
      return;
    }

    const element = document.getElementById(widgetId);
    if (!element) return;

    //The react grid element has the width styles
    const parentElement: HTMLElement | null = element.closest('.react-grid-item');
    const clone = element.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return;

    // Find actions INSIDE this widget
    const actions = element.querySelector('[data-card-actions]');

    if (actions && actions instanceof HTMLElement) {
      const actionsClone = actions.cloneNode(true);

      if (actionsClone instanceof HTMLElement) {
        actionsClone.style.position = 'absolute';
        actionsClone.style.top = '12px';
        actionsClone.style.right = '12px';
        actionsClone.style.zIndex = '10000';

        clone.style.position = 'relative';
        clone.appendChild(actionsClone);
      }
    }

    clone.style.position = 'absolute';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.height = 'auto';
    clone.style.background = 'white';
    clone.style.overflow = 'visible';
    clone.style.zIndex = '9999';
    clone.style.width =
      parentElement && parentElement.style.width !== '' ? parentElement.style.width : 'auto';

    hideNonExportableMenuIcons(clone);

    clone.querySelectorAll('svg').forEach((svg) => {
      if (svg instanceof SVGElement && !isStatTestingArrow(svg)) {
        svg.setAttribute('fill', '#00A0AF');
        svg.style.fill = '#00A0AF';
        svg.style.color = '#00A0AF';
      }
    });

    document.body.appendChild(clone);

    await new Promise((resolve) => requestAnimationFrame(resolve));

    const canvas = await html2canvas(clone, {
      backgroundColor: '#ffffff',
      scale: window.devicePixelRatio,
    });

    const image = isJpeg ? canvas.toDataURL('image/jpeg', 0.9) : canvas.toDataURL('image/png');

    const link = document.createElement('a');
    link.href = image;
    link.download = isJpeg ? `${name}.jpeg` : `${name}.png`;
    link.click();

    // Remove clone after capturing
    document.body.removeChild(clone);
  } catch (error) {
    console.log('Error during export to image.');
  }
};

const handleCapture = async (id: any, name = 'chart', isJpeg = false) => {
  try {
    const element = document.getElementById(id);
    if (!(element instanceof HTMLElement)) return;

    // Ensure fonts are loaded (critical for font mismatch)
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      scrollX: 0,
      scrollY: -window.scrollY,

      // Let html2canvas handle cloning safely
      onclone: (doc) => {
        const cloned = doc.getElementById(id);
        if (!cloned) return;

        cloned.querySelectorAll('svg text').forEach((text) => {
          if (text instanceof SVGTextElement) {
            text.style.fontFamily =
              "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial";
            text.style.fontWeight = '700';
          }
        });

        // Make sure everything is visible
        cloned.style.position = 'relative';
        cloned.style.overflow = 'visible';
        cloned.style.transform = 'none';

        // Disable animations/transitions (prevents iframe mismatch)
        cloned.querySelectorAll('*').forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.animation = 'none';
            el.style.transition = 'none';
          }
        });
      },
      ignoreElements: (el) =>
        el.classList?.contains('meatball-export-menu') || el.classList?.contains('calendar'),
    });

    const image = isJpeg ? canvas.toDataURL('image/jpeg', 0.9) : canvas.toDataURL('image/png');

    // Download
    const link = document.createElement('a');
    link.href = image;
    link.download = `${name}.${isJpeg ? 'jpeg' : 'png'}`;
    link.click();
  } catch (error) {
    console.error('Error capturing the element:', error);
  }
};
